// MSMEOwnerPAHome.jsx
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMicrophone
  } from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import MSMEOwnerAI from './MSMEOwnerAI';
import AILightComponent from '../../components/AILightComponent';
import SpeechInputComponent from '../../components/SpeechInputComponent';
import { GenerateLCCode } from '../../components/GenerateThink5whysCode';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

/* =========================
   MULTILINGUAL SUPPORT
========================= */
import msmeOwnerPATranslations from './MSMEOwnerPATranslations';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'മലയാളം' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'as', label: 'অসমীয়া' },
];

const topMsmeLeaders = [
  {
    name: "Rajesh Verma",
    place: "Pune",
    points: 9850,
  },
  {
    name: "Anita Sharma",
    place: "Indore",
    points: 9480,
  },
  {
    name: "Vikram Patel",
    place: "Ahmedabad",
    points: 9315,
  },
  {
    name: "Neha Kulkarni",
    place: "Nagpur",
    points: 9140,
  },
  {
    name: "Sandeep Rao",
    place: "Hyderabad",
    points: 8960,
  },
  {
    name: "Mohit Jain",
    place: "Jaipur",
    points: 8820,
  },
  {
    name: "Kiran Nair",
    place: "Bengaluru",
    points: 8675,
  },
  {
    name: "Priya Deshmukh",
    place: "Mumbai",
    points: 8540,
  },
  {
    name: "Ashok Gupta",
    place: "Delhi",
    points: 8425,
  },
  {
    name: "Ramesh Iyer",
    place: "Chennai",
    points: 8290,
  },
];

// /* =========================
//    SEED DATA
// ========================= */
// const [todos, setTodos] = useState([
//   {
//     id: 1,
//     title: t('todo1Title'),
//     desc: t('todo1Desc'),
//     category: t('operations'),
//     department: t('operations'),
//     priority: 'High',
//     tts: true,
//     screenAlert: true,
//     whatsappAlert: true,
//     emailAlert: false,
//     linkedTodoId: 'TODO-1001',
//     rcaId: 'RCA-5001',
//     remarks: 'Critical operational dependency.',
//     tags: [t('reminderEnabled'), t('whatsappAlert'), t('rcaLinked')],
//   },
//   {
//     id: 2,
//     title: t('todo2Title'),
//     desc: t('todo2Desc'),
//     category: t('finance'),
//     department: t('finance'),
//     priority: 'Medium',
//     tts: true,
//     screenAlert: true,
//     whatsappAlert: false,
//     emailAlert: true,
//     linkedTodoId: 'TODO-1002',
//     rcaId: 'RCA-5002',
//     remarks: 'Pending invoice reconciliation.',
//     tags: [t('aiReminder'), t('emailNotification')],
//   },
//   {
//     id: 3,
//     title: t('todo3Title'),
//     desc: t('todo3Desc'),
//     category: t('production'),
//     department: t('production'),
//     priority: 'High',
//     tts: true,
//     screenAlert: true,
//     whatsappAlert: true,
//     emailAlert: true,
//     linkedTodoId: 'TODO-1003',
//     rcaId: 'RCA-5003',
//     remarks: 'Corrective action still pending.',
//     tags: [t('correctiveActionPending')],
//   },
// ]);

export default function MSMEOwnerPAHome() {

  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const [todo, setTodo] = useState(null);
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [autoTodoCode, setAutoTodoCode] = useState(GenerateLCCode());
  
  // const editFormRef = useRef(null);
  const taskTitleRef = useRef(null);
  const listSectionRef = useRef(null);
  const createSectionRef = useRef(null);
  const aiInsightsSectionRef = useRef(null);
  const reportsSectionRef = useRef(null);
  const rewardsSectionRef = useRef(null);

  const [activeTab, setActiveTab] = useState('List');
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  // Form States
  const [todoDate, setTodoDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDetails, setTaskDetails] = useState('');
  const [priority, setPriority] = useState("Medium");
  const [department, setDepartment] = useState("No Department");
  const [screenAlert, setScreenAlert] = useState(true);
  const [whatsappAlert, setWhatsappAlert] = useState(false);
  const [emailAlert, setEmailAlert] = useState(false);
  const [linkedTodoId, setLinkedTodoId] = useState("");
  const [rcaId, setRcaId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [whoSuggested, setWhoSuggested] = useState("Self-created");
  const [dateFilter, setDateFilter] = useState("ALL"); // ALL | YESTERDAY | TODAY | TOMORROW | OTHER
  const [status, setStatus] = useState("Planned");
  const [deferredDate, setDeferredDate] = useState("");

  // Editing State
  const [isEditing, setIsEditing] = useState(false);
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // UI States
  const [activeAssistantTodoId, setActiveAssistantTodoId] = useState(null);
  const [activeAssistantCreateTodo, setActiveAssistantCreateTodo] = useState(null);
  const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);
  const [showAlertSettings, setShowAlertSettings] = useState(true);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isVoicePaused, setIsVoicePaused] = useState(false);
  const [recognitionInstance, setRecognitionInstance] = useState(null);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const [isVoiceListeningPaused, setIsVoiceListeningPaused] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [activeTtsId, setActiveTtsId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formErrors, setFormErrors] = useState({});

  const t = (key) => msmeOwnerPATranslations[selectedLanguage]?.[key] || key;

  const dashboardStats = [
    {
      title: t('pendingTasks'),
      value: '27',
      icon: 'fa-list-check',
      growth: '+8%',
    },
    {
      title: t('taskCompletionRatio'),
      value: '82%',
      icon: 'fa-chart-line',
      growth: '+14%',
    },
    {
      title: t('rcaEntries'),
      value: '11',
      icon: 'fa-chart-column',
      growth: '+5%',
    },
  ];

  const myRanking = [
    {
      title: t('sakshamRanking'),
      value: '#38',
      icon: 'fa-trophy',
      growth: '+9%',
    },
  ];

  const isSameDay = (d1, d2) =>
    new Date(d1).toDateString() === new Date(d2).toDateString();

  const getFilteredTodos = () => {
    const today = new Date();

    return todos.filter((todo) => {
      if (!todo.todoDate) return dateFilter === "ALL";

      const todoDate = new Date(todo.todoDate);

      const diffTime = new Date(
        todoDate.getFullYear(),
        todoDate.getMonth(),
        todoDate.getDate()
      ).getTime() -
        new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (dateFilter === "TODAY") return diffDays === 0;
      if (dateFilter === "YESTERDAY") return diffDays === -1;
      if (dateFilter === "TOMORROW") return diffDays === 1;
      if (dateFilter === "OTHER") return diffDays < -1 || diffDays > 1;

      return true;
    });
  };

  const STATUS_ORDER = [
    "Planned",
    "In Progress",
    "Done",
    "Deferred",
    "Cancelled",
  ];

  const groupByStatus = (list) => {
    const grouped = {};
    STATUS_ORDER.forEach((s) => (grouped[s] = []));
    list.forEach((todo) => {
      const status = todo.status || "Planned";
      if (!grouped[status]) grouped[status] = [];
      grouped[status].push(todo);
    });
    // sort inside each group by date/time
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) => new Date(b.todoDate) - new Date(a.todoDate)
      );
    });
    return grouped;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      const sectionMap = {
        [t('list')]: listSectionRef,
        [t('create')]: createSectionRef,
        [t('aiInsights')]: aiInsightsSectionRef,
        [t('reports')]: reportsSectionRef,
        [t('myRank')]: rewardsSectionRef,
        [t('rewards')]: rewardsSectionRef,
      };
      const selectedRef = sectionMap[tab];
      selectedRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

    }, 100);
  };

  const [todos, setTodos] = useState([]);

  const fetchMyTodos = async () => {
  try {
    const config = {
      url: "/tasks/mytodos",
      method: "post",
      data: {
        userId: userId,
      },
      headers: {
        Authorization: authState.token,
      },
    };

    const response = await fetchData(config);
      if (response?.status) {
        const formattedTodos = response.tasks.map((todo) => ({
          id: todo._id,
          todoDate: todo.todoDate ? todo.todoDate.split("T")[0] : "",
          title: todo.title,
          desc: todo.description,
          category: todo.department,
          department: todo.department,
          priority: todo.priority,
          tts: true,
          screenAlert: todo.alerts?.screen?.enabled || false,
          whatsappAlert: todo.alerts?.whatsapp?.enabled || false,
          emailAlert: todo.alerts?.email?.enabled || false,
          linkedTodoId: todo.linkedTodoId || "",
          rcaId: todo.rcaId || "",
          remarks: todo.remarks || "",
          whoSuggested: todo.whoSuggested || "Self-created",
          alerts: todo.alerts || {},
          tags: todo.tags || [],
        }));
        setTodos(formattedTodos);
      }
    } catch (error) {
      console.error("Fetch Todo Error:", error);
      showMessage("Failed to load To-Dos", "error");
    }
  };

  // useEffect(() => {
  //   if (userId) {
  //     fetchMyTodos();
  //   }
  // }, [userId]);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!userId) return;

    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchMyTodos();
  }, [userId]);


  const tabs = [
    t('list'),
    t('create'),
    t('aiInsights'),
    t('reports'),
    t('myRank'),
    t('rewards'),
  ];

  // ====================== TTS ======================
  const speakText = (todo) => {
    if (!window.speechSynthesis) {
      showMessage("Text to Speech is not supported in this browser", "error");
      return;
    }
    speechSynthesis.cancel();

    const text = `Title: ${todo.title}. Category: ${todo.category}. Description: ${todo.desc}.`;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = selectedLanguage === "hi" ? "hi-IN" : "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onstart = () => { setIsSpeaking(true); setIsPaused(false); setActiveTtsId(todo._id); };
    utterance.onend = () => { setIsSpeaking(false); setIsPaused(false); setActiveTtsId(null); };
    utterance.onerror = () => { setIsSpeaking(false); setIsPaused(false); setActiveTtsId(null); };
    utterance.onerror = () => { setIsSpeaking(false); setIsPaused(false); setActiveTtsId(null); showMessage("Error occurred while speaking", "error");};
    speechSynthesis.speak(utterance);
  };

  const pauseSpeech = () => { speechSynthesis.pause(); setIsPaused(true); };
  const resumeSpeech = () => { speechSynthesis.resume(); setIsPaused(false); };
  const stopSpeech = () => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
    setActiveTtsId(null);
  };

  // ====================== FORM HANDLERS ======================
  const showMessage = (text, type = "success") => {
    setMessage({ type, text });
  };

  const resetForm = () => {
    setTodoDate(null);
    setTaskTitle("");
    setTaskDetails("");
    setPriority("Medium");
    setDepartment("No Department");

    setScreenAlert(true);
    setWhatsappAlert(false);
    setEmailAlert(false);

    setLinkedTodoId("");
    setRcaId("");
    setRemarks("");
    setWhoSuggested("Self-created");
    setStatus("Planned");
    setDeferredDate("");
    setIsEditing(false);
    setEditingTodoId(null);
  };

  const handleEditTodo = (todo) => {
    setIsEditing(true);
    setEditingTodoId(todo.id);
    setTodoDate(todo.todoDate ? todo.todoDate.split("T")[0] : "");
    setTaskTitle(todo.title || "");
    setTaskDetails(todo.desc || "");
    setPriority(todo.priority || "Medium");
    setDepartment(todo.department || "");

    setScreenAlert(todo?.alerts?.screen?.enabled || false);
    setWhatsappAlert(todo?.alerts?.whatsapp?.enabled || false);
    setEmailAlert(todo?.alerts?.email?.enabled || false);

    setLinkedTodoId(todo.linkedTodoId || "");
    setRcaId(todo.rcaId || "");
    setRemarks(todo.remarks || "");
    setWhoSuggested(todo.whoSuggested || "Self-created");
    setStatus(todo.status || "Planned");
    setDeferredDate(todo.deferredDate || "");
    setTimeout(() => {
      createSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      taskTitleRef.current?.focus();
    }, 200);
  };

  const handleSaveTodo = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    const errors = {};
    if (!taskTitle.trim()) {
      errors.title = "Title is required";
    }
    if (!taskDetails.trim()) {
      errors.description = "Description is required";
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      showMessage("Please fix validation errors", "error");
      return;
    }
    
    setIsSaving(true);
    try {
      const payload = {
        user: userId,
        userCode: userCode,
        todoCode: autoTodoCode,
        todoDate: todoDate,
        title: taskTitle.trim(),
        description: taskDetails.trim(),
        priority,
        department: department || "General",
        linkedTodoId,
        rcaId,
        alerts: {
          screen: { enabled: screenAlert },
          whatsapp: { enabled: whatsappAlert },
          email: { enabled: emailAlert },
        },
        remarks,
        whoSuggested,
        status,
        deferredDate: status === "Deferred" ? deferredDate : null,
        isToDo: true,
        tags: [
          priority,
          department,
          screenAlert && "Screen Alert",
          whatsappAlert && "WhatsApp Alert",
          emailAlert && "Email Alert",
        ].filter(Boolean),
      };

      const config = {
        url: "/tasks/createtodo",
        method: "post",
        data: payload,
        headers: {
          Authorization: authState.token,
        },
      };
      const response = await fetchData(config);
      if (response?.status) {
        await fetchMyTodos();
        showMessage("To-Do Saved Successfully");
        setAutoTodoCode(GenerateLCCode());
        resetForm();
      }
    } catch (error) {
        console.error("FULL ERROR:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save To-Do";
        showMessage(errorMessage, "error");
    } finally {
    setIsSaving(false);
  }
  };

  const handleUpdateTodo = async () => {
    if (!taskTitle.trim() || !taskDetails.trim()) {
      showMessage("Title & Description are required", "error");
      return;
    }
    try {
      const payload = {
        todoDate: todoDate,
        title: taskTitle,
        description: taskDetails,
        priority,
        department: department || "General",
        linkedTodoId,
        rcaId,
        remarks,
        whoSuggested: whoSuggested || "Self-created",
        status,
        deferredDate: status === "Deferred" ? deferredDate : null,
        alerts: {
          screen: { enabled: screenAlert },
          whatsapp: { enabled: whatsappAlert },
          email: { enabled: emailAlert },
        },
        tags: [
          priority,
          department,
          screenAlert ? "Screen Alert" : null,
          whatsappAlert ? "WhatsApp Alert" : null,
          emailAlert ? "Email Alert" : null,
        ].filter(Boolean),
      };
      const config = {
        url: `/tasks/todo/${editingTodoId}`,
        method: "put",
        data: payload,
        headers: {
          Authorization: authState.token,
        },
      };
      const response = await fetchData(config);
      if (response?.status) {
        setTodos((prev) =>
          prev.map((todo) =>
            todo._id === editingTodoId
              ? { ...todo, ...payload }
              : todo
          )
        );
        await fetchMyTodos();
        showMessage("To-Do Updated Successfully");
        resetForm();
      }
    } catch (error) {
      console.error(error);
      showMessage(
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to update To-Do",
        "error"
      );
    }
  };

  const handleDeleteTodo = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const config = {
        url: `/tasks/todo/${editingTodoId}`,
        method: "delete",
        headers: {
          Authorization: authState.token,
        },
      };
      const response = await fetchData(config);
      if (response?.status) {
        await fetchMyTodos();
        resetForm();
        showMessage("To-Do Deleted Successfully");
      }
    } catch (error) {
      console.error("DELETE TODO ERROR:", error);
      showMessage(
        error?.response?.data?.msg ||
        error?.message ||
        "Failed to delete To-Do",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelTodo = () => resetForm();

  const toggleVoiceInput = () => {
    if (isVoiceListening) {
      stopVoiceInput();
      return;
    }
    startVoiceInput();
  };

  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showMessage("Speech Recognition not supported in this browser", "error");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";           // Change to "hi-IN" if needed
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        setTaskDetails(prev => prev + " " + transcript);
      }
    };

    recognition.onerror = (event) => {
      console.error(event);
      if (event.error !== "no-speech") {
        showMessage("Voice recognition error. Please try again.", "error");
        stopVoiceInput();
      }
    };

    recognition.onend = () => {
      setIsVoiceActive(false);
      setIsVoicePaused(false);
    };

    try {
      recognition.start();
      setRecognitionInstance(recognition);
      setIsVoiceActive(true);
      setIsVoicePaused(false);
      
      // Auto focus on textarea
      document.getElementById("taskDetails")?.focus();
      
      showMessage("🎤 Listening... Speak now", "success");
    } catch (err) {
      showMessage("Failed to start voice input", "error");
    }
  };

  const pauseVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoicePaused(true);
    showMessage("Voice input paused", "success");
  };

  const resumeVoiceInput = () => {
    setIsVoicePaused(false);
    startVoiceInput();
  };

  const stopVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoiceActive(false);
    setIsVoicePaused(false);
    setRecognitionInstance(null);
    showMessage("Voice input stopped", "success");
  };

  const handleMicrophoneClick = () => {
    setShowMicrophoneAlert(true);
  };

  // ====================== CHART ======================
  const chartData = useMemo(() => ({
    labels: [t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    datasets: [
      {
        label: t('taskCompletionPercent'),
        data: [65, 72, 70, 80, 82, 88],
        borderColor: '#14b8a6',
        backgroundColor: 'rgba(20,184,166,0.18)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#fde68a',
      },
    ],
  }), [selectedLanguage]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#fde68a' } },
    },
    scales: {
      x: { ticks: { color: '#d1d5db' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { beginAtZero: true, ticks: { color: '#d1d5db', stepSize: 20 }, grid: { color: 'rgba(255,255,255,0.05)' } },
    },
  }), []);

  const filtered = getFilteredTodos();
  const grouped = groupByStatus(filtered);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-950 to-slate-900 p-3 text-gray-100 sm:p-5">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-r from-black via-slate-950 to-slate-900 p-5 shadow-2xl sm:p-8">
          <div className="flex flex-col gap-2 justify-start lg:flex-row">
            <div className="flex-1 text-left justify-between">
              <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 text-xs text-gray-200 backdrop-blur-sm">
                  <i className="fa-solid fa-bolt text-yellow-300"></i>
                  {t("dailyProductivityAI")}
                </div>
              </div>
              <h1 className="text-left text-xl lg:text-2xl font-black leading-tight text-yellow-200">MSME OwnerPA</h1>
              <p className="mt-2 text-left text-xs leading-7 text-gray-300 lg:text-sm">{t('heroDescription')}</p>
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-teal-700/20 p-3 text-teal-300">
                  <i className="fa-solid fa-microchip text-lg lg:text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">{t('aiStack')}</p>
                  <p className="text-[9px] lg:text-lg font-bold text-yellow-200">{t('aiStackValue')}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-emerald-700/20 p-3 text-emerald-300">
                  <i className="fa-solid fa-microphone-lines text-lg lg:text-xl"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">{t('voiceAI')}</p>
                  <p className="text-[9px] lg:text-lg font-bold text-yellow-200">STT + TTS</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <label className="text-xs sm:text-sm font-medium text-gray-200">
              Select Your Language:
            </label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full sm:w-48 rounded-xl border border-slate-700 bg-slate-900 px-3 py-1 text-xs lg:text-sm text-white outline-none transition-all focus:border-teal-500"
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
            </div>
        </div>

        {/* Tabs */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900 p-3 shadow-xl">
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex w-full items-center justify-center rounded-md px-2 py-1 text-center text-xs font-semibold transition-all lg:text-sm ${
                  activeTab === tab ? 'bg-teal-700 text-yellow-200 shadow-lg shadow-teal-900/30' : 'bg-slate-800 text-gray-200 hover:bg-slate-700 hover:text-yellow-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          <div className="space-y-5 xl:col-span-2">
            {/* ====================== TODO LIST ====================== */}
            <div ref={listSectionRef} className="rounded-xl border border-slate-800 bg-slate-700 p-2 shadow-2xl">
              <div className="mb-2 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg lg:text-xl  font-black text-yellow-200">{t('todoList')}</h2>
                  <p className="mt-1 text-sm text-gray-300">{t('todoListDesc')}</p>
                </div>
                <button 
                    onClick={() => { 
                      setActiveTab(t('create'));
                      setTimeout(() => {
                        createSectionRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });

                      }, 100);
                  }}
                  className="w-20 lg:w-32 text-xs lg:text-sm rounded-2xl bg-teal-700 px-1 py-1 font-bold text-white shadow-lg transition-all hover:bg-teal-600"
                >
                  <i className="fa-solid fa-plus mr-2"></i>{t('addTask')}
                </button>
              </div>

              <div className="mb-1 grid grid-cols-5 gap-1">
                {["ALL", "TODAY", "YESTERDAY", "TOMORROW", "OTHER"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setDateFilter(f)}
                    className={`rounded-xl px-1 py-1 text-xs font-bold transition-all
                      ${dateFilter === f
                        ? "bg-teal-600 text-white"
                        : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                      }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 lg:grid-cols-5">
                {dashboardStats.map((stat) => (
                  <div key={stat.title} className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow-xl transition-all hover:-translate-y-1 hover:border-teal-500">
                    <div className="flex items-start justify-between">
                      <div className="rounded-2xl bg-slate-800 p-3 text-yellow-200">
                        <i className={`fa-solid ${stat.icon} text-lg`}></i>
                      </div>
                      <span className="rounded-full bg-emerald-700/20 px-2 py-1 text-xs font-semibold text-emerald-300">{stat.growth}</span>
                    </div>
                    <h2 className="mt-2 text-sm lg:text-lg font-black text-yellow-200 sm:text-3xl">{stat.value}</h2>
                    <p className="mt-1 text-xs text-gray-300 sm:text-sm">{stat.title}</p>
                  </div>
                ))}
              </div>
              <div>
                <button
                  onClick={() => speakText(filtered.map(t => t.title).join(". "))}
                  className="rounded-xl bg-yellow-600 px-3 py-1 text-xs font-bold text-black hover:bg-yellow-500"
                >
                  <i className="fa-solid fa-volume-high mr-2"></i>
                  Listen List
                </button>
              </div>

              <div className="space-y-4">
                {todos.map((todo) => (
                  <div key={todo._id} className="rounded-3xl border border-slate-800 bg-slate-800 p-5 transition-all hover:border-teal-500 hover:shadow-lg">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex-1">
                        <div className="mb-2 text-xs text-teal-300">
                          <i className="fa-regular fa-calendar mr-1"></i>
                          {todo.todoDate
                            ? new Date(todo.todoDate).toLocaleDateString("en-GB")
                            : "No Date"}
                          <span className="rounded-full bg-sky-700/20 px-3 py-1 text-xs font-semibold text-sky-300">
                            {todo.currentStatus}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-xs lg:text-lg font-bold text-yellow-200">{todo.title}</h3>
                          {todo.tts && (
                            <span className="rounded-full bg-orange-700/20 px-3 py-1 text-xs font-semibold text-orange-300">
                              <i className="fa-solid fa-volume-high mr-1"></i>{t('ttsEnabled')}
                            </span>
                          )}
                          <span className="rounded-full bg-sky-700/20 px-3 py-1 text-xs font-semibold text-sky-300">
                            {todo.category}
                          </span>
                        </div>
                        <p className="mt-3 text-sm leading-6 text-gray-300">{todo.desc}</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {todo.tags?.map((tag, i) => (
                            <span key={i} className="rounded-full bg-slate-800 px-3 py-1 text-xs font-medium text-gray-100">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <div className="flex gap-2">
                          {activeTtsId !== todo._id ? (
                            <button onClick={() => speakText(todo)} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : isPaused ? (
                            <button onClick={resumeSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-yellow-700 px-1 py-1 text-black hover:bg-yellow-600">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : (
                            <>
                              <button onClick={pauseSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-blue-700 px-1 py-1 text-white hover:bg-blue-600">
                                <i className="fa-solid fa-pause"></i>
                              </button>
                              <button onClick={stopSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-red-700 px-1 py-1 text-white hover:bg-red-600">
                                <i className="fa-solid fa-stop"></i>
                              </button>
                            </>
                          )}
                        </div>
                        <button onClick={() => handleEditTodo(todo)} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                          <i className="fa-solid fa-pen"></i>
                        </button>
                        <button
                          onClick={() => setActiveAssistantTodoId(prev => prev === todo._id ? null : todo._id)}
                          className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700"
                        >
                          <i className="fa-solid fa-robot"></i>
                        </button>
                      </div>
                    </div>

                    {activeAssistantTodoId === todo._id && (
                      <div className="mt-2">
                        <AILightComponent
                          param1={"MSME Owner Requires Assistance"}
                          param2={`To-Do Title: ${todo.title} Department: ${todo.department} Priority: ${todo.priority} Description: ${todo.desc}`}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ====================== CREATE / EDIT FORM ====================== */}
            <div ref={createSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-2 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-square-plus text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-lg lg:text-xl font-black text-yellow-200">
                    {isEditing ? "Edit To-Do" : t("createTodo")}
                  </h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">
                    {isEditing ? "Update the selected task" : t("createTodoDesc")}
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                <div className='border border-blue-100 p-4 rounded-xl'>
                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                      {t("todoDate")}*
                      {/* Date* */}
                    </label>

                    <input
                      type="date"
                      value={todoDate}
                      onChange={(e) => setTodoDate(e.target.value)}
                      className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">Title*</label>
                    <input
                      type="text"
                      ref={taskTitleRef}
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      placeholder={"Enter Task Title"}
                      className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">Priority*</label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">Department*</label>
                    <select value={department} onChange={(e) => setDepartment(e.target.value)} className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500">
                      <option value="">Select Department</option>
                      <option>No Department </option>
                      <option>Administration</option>
                      <option>Production / Manufacturing</option>
                      <option>Quality Control</option>
                      <option>Research & Development (R&D)</option>
                      <option>Purchase / Procurement</option>
                      <option>Inventory / Stores</option>
                      <option>Warehouse & Dispatch</option>
                      <option>Logistics & Supply Chain</option>
                      <option>Sales</option>
                      <option>Marketing</option>
                      <option>Digital Marketing</option>
                      <option>Customer Support</option>
                      <option>Service Operations</option>
                      <option>Accounts & Finance</option>
                      <option>GST & Taxation</option>
                      <option>Human Resources (HR)</option>
                      <option>Training & Development</option>
                      <option>Legal & Compliance</option>
                      <option>Information Technology (IT)</option>
                      <option>Cyber Security</option>
                      <option>Business Development</option>
                      <option>Projects</option>
                      <option>Maintenance</option>
                      <option>Safety & EHS</option>
                      <option>Export / Import</option>
                      <option>Retail Operations</option>
                      <option>E-Commerce</option>
                      <option>Franchise Operations</option>
                      <option>Vendor Management</option>
                      <option>Strategy & Planning</option>
                      <option>AI & Automation</option>
                      <option>MSME Support Desk</option>
                      <option>Founder Office</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className='w-6/12'>
                    <input 
                      type="checkbox" 
                      className="mr-2 cursor-pointer"
                      checked={showMicrophoneAlert} 
                      onChange={() => setShowMicrophoneAlert(!showMicrophoneAlert)} 
                    />
                    <span className="text-xs lg:text-sm font-medium text-gray-100">Enable Voice Input</span>
                    <FontAwesomeIcon icon={faMicrophone} className="text-yellow-500 text-lg lg:text-xl ml-3" />
                  </div>
                  <div className="mb-2 text-xs lg:text-sm text-white">
                      <p className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">Details*:</p>
                      { showMicrophoneAlert ? (
                          <SpeechInputComponent
                            label="Task Details"
                            name="taskDetails"
                            value={taskDetails}
                            onChange={(e) => setTaskDetails(e.target.value)}
                            placeholder="Click Mic and Speak or Talk for Voice Input..."
                            maxLength="2000"
                          />
                      ) : (
                        <textarea 
                          type="input" 
                          name="taskDetails" 
                          id="taskDetails" 
                          value={taskDetails}
                          placeholder={showMicrophoneAlert ? "Write here or Click Mic for Voice Input...":"Write here.."}
                          onChange={(e) => setTaskDetails(e.target.value)}
                          maxLength="2000"
                          style={{
                            border: '1px solid #ccc',
                            padding: '2px',
                            backgroundColor: '#000000', 
                            color: '#f0f0f0',
                            maxWidth: '100%', 
                            maxHeight: '150px' 
                          }}
                        />
                      )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {/* AI Refine Button */}
                    <button
                      onClick={() => setActiveAssistantCreateTodo(!activeAssistantCreateTodo)}
                      className="rounded-2xl bg-teal-700 px-5 py-3 font-bold text-white shadow-lg transition-all hover:bg-teal-600 w-40 lg:w-64 text-xs lg:text-sm"
                    >
                      <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
                      {t("refineWithAI")}
                    </button>
                  </div>
                </div>

                {/* AI Refinement Section */}
                {activeAssistantCreateTodo && (
                  <>
                    {(!taskTitle.trim() || !taskDetails.trim() || !priority || !department) ? (
                      <div className="mt-4 rounded-2xl border border-red-500 bg-red-500/10 p-4">
                        <div className="flex items-start gap-3 text-red-300">
                          <i className="fa-solid fa-circle-exclamation mt-1"></i>
                          <div>
                            <p className="font-bold text-yellow-200">Missing Required Information</p>
                            
                            <p className="mt-1 text-xs lg:text-sm text-red-200">
                              Please fill the following field{ 
                                [taskTitle.trim(), taskDetails.trim(), priority, department].filter(Boolean).length === 3 ? '' : 's'
                              }:
                            </p>

                            <ul className="mt-2 text-xs lg:text-sm text-red-200 [&_li]:text-gray-100 list-disc list-inside space-y-1">
                              {!taskTitle.trim() && <li className='text-xs lg:text-sm'><strong>Task Title</strong></li>}
                              {!taskDetails.trim() && <li className='text-xs lg:text-sm'><strong>Task Description</strong></li>}
                              {!priority && <li className='text-xs lg:text-sm'><strong>Priority</strong></li>}
                              {!department && <li className='text-xs lg:text-sm'><strong>Department</strong></li>}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <AILightComponent
                          param1="MSME To-Do Task"
                          param2={`Title: ${taskTitle}
                          Department: ${department}
                          Priority: ${priority}
                          Description: ${taskDetails}`}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="mb-2 block text-xs font-bold text-yellow-200">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100 outline-none focus:border-teal-500"
                    >
                      <option value="Planned">Planned</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Done">Done</option>
                      <option value="Deferred">Deferred</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  {status === "Deferred" && (
                    <div>
                      <label className="mb-2 block text-xs font-bold text-yellow-200">
                        Deferred Date
                      </label>

                      <input
                        type="date"
                        value={deferredDate}
                        onChange={(e) => setDeferredDate(e.target.value)}
                        className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    Who Suggested
                  </label>
                  <input
                    type="text"
                    value={whoSuggested}
                    onChange={(e) => setWhoSuggested(e.target.value)}
                    placeholder="e.g. Grok, ChatGPT, Claude, Self, Manager, etc."
                    className="w-full text-xs lg:text-sm rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                  />
                </div>
                <div className="grid gap-1 grid-cols-1 lg:grid-cols-2 border border-blue-100 p-4 rounded-xl">
                  <div>
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Linked To-Do ID</label>
                    <input type="text" value={linkedTodoId} onChange={(e) => setLinkedTodoId(e.target.value)} placeholder="Enter Linked To-Do ID" className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500" />
                  </div>
                  <div>
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">RCA ID</label>
                    <input type="text" value={rcaId} onChange={(e) => setRcaId(e.target.value)} placeholder="Enter RCA ID" className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500" />
                  </div>
                </div>

                <div>
                  <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">Additional Remarks</label>
                  <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter Remarks" style={{ height: "150px" }} className="w-full text-xs lg:text-sm rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500" />
                </div>

                <div className='border border-blue-100 p-4 rounded-xl'>
                  <div onClick={() => setShowAlertSettings(!showAlertSettings)}
                      className="mb-3 flex cursor-pointer items-center justify-between rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 transition-all hover:border-teal-500 hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-yellow-500/10 p-2 text-yellow-300">
                          <i className="fa-solid fa-bell"></i>
                        </div>
                        <label className="cursor-pointer text-xs lg:text-sm font-bold text-yellow-200">
                          Alerts & Notifications
                        </label>
                      </div>
                      <div className="text-teal-300">
                        <i
                          className={`fa-solid ${
                            showAlertSettings
                              ? "fa-chevron-up"
                              : "fa-chevron-down"
                          }`}
                        ></i>
                      </div>
                    </div>
                  {showAlertSettings && (
                    <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4 shadow-2xl">
                      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
                        <div className="rounded-2xl border border-slate-700 bg-slate-950 p-4">
                          <label className="flex items-center gap-3 text-xs lg:text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={screenAlert}
                              onChange={(e) => setScreenAlert(e.target.checked)}
                            />
                            AI Shall Alert on Screen
                          </label>
                          {screenAlert && (
                            <div className="mt-4 space-y-3">
                              <select
                                className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-1 text-xs lg:text-sm text-white"
                              >
                                <option value="">Select Frequency</option>
                                <option value="Daily">Daily</option>
                                <option value="Weekly">Weekly</option>
                              </select>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-400">
                                  Select Alert Time(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {Array.from({ length: 24 }).map((_, hour) => (
                                    <label
                                      key={hour}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {hour.toString().padStart(2, "0")}:00
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-400">
                                  Select Day(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    "Monday",
                                    "Tuesday",
                                    "Wednesday",
                                    "Thursday",
                                    "Friday",
                                    "Saturday",
                                    "Sunday",
                                  ].map((day) => (
                                    <label
                                      key={day}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {day}
                                    </label>
                                  ))}
                                </div>
                                <p className="text-xs text-gray-400 mt-3">
                                  Select Weekly Alert Time(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {Array.from({ length: 24 }).map((_, hour) => (
                                    <label
                                      key={hour}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {hour.toString().padStart(2, "0")}:00
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="rounded-2xl border border-slate-700 bg-slate-950 p-4">
                          <label className="flex items-center gap-3 text-xs lg:text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={whatsappAlert}
                              onChange={(e) => setWhatsappAlert(e.target.checked)}
                            />
                            Get WhatsApp Alert
                          </label>
                          {whatsappAlert && (
                            <div className="mt-4 space-y-4">
                              <select className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-1 text-xs lg:text-sm text-white">
                                <option value="">Select Frequency</option>
                                <option value="Daily">Daily</option>
                                <option value="Weekly">Weekly</option>
                              </select>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-400">
                                  Select WhatsApp Alert Time(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {Array.from({ length: 24 }).map((_, hour) => (
                                    <label
                                      key={hour}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {hour.toString().padStart(2, "0")}:00
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-400">
                                  Select Day(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    "Monday",
                                    "Tuesday",
                                    "Wednesday",
                                    "Thursday",
                                    "Friday",
                                    "Saturday",
                                    "Sunday",
                                  ].map((day) => (
                                    <label
                                      key={day}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {day}
                                    </label>
                                  ))}
                                </div>
                                <p className="mt-3 text-xs text-gray-400">
                                  Select Weekly Alert Time(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {Array.from({ length: 24 }).map((_, hour) => (
                                    <label
                                      key={hour}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {hour.toString().padStart(2, "0")}:00
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="rounded-2xl border border-slate-700 bg-slate-950 p-4">
                          <label className="flex items-center gap-3 text-xs lg:text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={emailAlert}
                              onChange={(e) => setEmailAlert(e.target.checked)}
                            />
                            Get Email Notification
                          </label>
                          {emailAlert && (
                            <div className="mt-4 space-y-4">
                              <select className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-1 text-xs lg:text-sm text-white">
                                <option value="">Select Frequency</option>
                                <option value="Daily">Daily</option>
                                <option value="Weekly">Weekly</option>
                              </select>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-400">
                                  Select Email Notification Time(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {Array.from({ length: 24 }).map((_, hour) => (
                                    <label
                                      key={hour}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {hour.toString().padStart(2, "0")}:00
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-400">
                                  Select Day(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    "Monday",
                                    "Tuesday",
                                    "Wednesday",
                                    "Thursday",
                                    "Friday",
                                    "Saturday",
                                    "Sunday",
                                  ].map((day) => (
                                    <label
                                      key={day}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />{day}
                                    </label>
                                  ))}
                                </div>
                                <p className="mt-3 text-xs text-gray-400">
                                  Select Weekly Alert Time(s)
                                </p>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {Array.from({ length: 24 }).map((_, hour) => (
                                    <label
                                      key={hour}
                                      className="flex items-center gap-2 rounded-lg border border-slate-700 p-2 text-xs text-gray-200"
                                    >
                                      <input type="checkbox" />
                                      {hour.toString().padStart(2, "0")}:00
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-2 grid grid-cols-4 lg:grid-cols-4 gap-1">
                <button
                  disabled={isEditing || isSaving}
                  onClick={handleSaveTodo}
                  className={`w-40 lg:w-48 text-xs lg:text-sm rounded-xl px-2 py-1 font-bold text-white shadow-lg transition-all
                    ${(isEditing || isSaving)
                      ? "cursor-not-allowed bg-slate-700 opacity-50"
                      : "bg-teal-600 hover:bg-teal-500"
                    }`}
                >
                  <i className="fa-solid fa-floppy-disk mr-2"></i>
                  {isSaving ? "Saving..." : "Save To-Do"}
                </button>

                {isEditing && (
                  <>
                    <button onClick={() => handleUpdateTodo()} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-yellow-600 px-2 py-1 font-bold text-black shadow-lg hover:bg-yellow-500">
                      <i className="fa-solid fa-rotate mr-2"></i> Update
                    </button>
                    <button onClick={handleDeleteTodo} disabled={isDeleting} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-red-700 px-2 py-1 font-bold text-white shadow-lg hover:bg-red-600">
                      <i className="fa-solid fa-trash mr-2"></i> Delete
                    </button>
                  </>
                )}

                <button onClick={handleCancelTodo} className="w-20 lg:w-32 text-xs lg:text-sm rounded-xl bg-slate-700 px-2 py-1 font-bold text-white shadow-lg hover:bg-slate-600">
                  <i className="fa-solid fa-xmark mr-2"></i> Cancel
                </button>
              </div>
              {/* Message Banner */}
              {message.text && (
                <div className={`rounded-2xl border px-6 py-4 text-xs lg:text-sm font-medium flex items-center gap-3 shadow-lg ${
                  message.type === 'success' 
                    ? 'bg-green-900/50 border-green-500 text-green-200' 
                    : 'bg-red-900/50 border-red-500 text-red-200'
                }`}>
                  <i className={`fa-solid ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                  <span>{message.text}</span>
                  <button 
                    onClick={() => setMessage({ type: '', text: '' })}
                    className="w-12 ml-auto text-lg p-2 bg-blue-600 text-white leading-none hover:text-white"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* =========================
                SAKSHAM MSME REWARDS
            ========================= */}
            <div ref={rewardsSectionRef} className="mt-6 rounded-3xl border border-slate-800 bg-slate-900 p-4 shadow-2xl sm:mt-8 sm:p-6">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 items-start">
                  {/* LEFT: STATS GRID */}
                  <div className="grid grid-cols-1">
                    {myRanking.map((stat) => (
                      <div
                        key={stat.title}
                        className="rounded-xl border border-slate-800 bg-slate-700 p-4 shadow-xl transition-all hover:-translate-y-1 hover:border-teal-500"
                      >
                        <div className="flex items-start justify-between">
                          <div className="rounded-2xl bg-slate-800 p-3 text-yellow-200">
                            <i className={`fa-solid ${stat.icon} text-lg`}></i>
                          </div>

                          <span className="rounded-full bg-emerald-700/20 px-2 py-1 text-xs font-semibold text-emerald-300">
                            {stat.growth}
                          </span>
                        </div>

                        <h2 className="mt-2 text-sm lg:text-lg font-black text-yellow-200 sm:text-3xl">
                          {stat.value}
                        </h2>

                        <p className="mt-1 text-xs text-gray-300 sm:text-sm">
                          {userName}{' - '}{stat.title}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* RIGHT: INFO PANEL */}
                  <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-xl">
                    <h2 className="text-sm lg:text-lg font-black text-yellow-200">
                      SAKSHAM MSME Rewards
                    </h2>

                    <p className="mt-2 text-xs lg:text-sm leading-6 text-gray-300">
                      AI-driven MSME operational excellence recognition ecosystem
                    </p>
                  </div>

                </div>
              </div>
              <div className="rounded-3xl border border-yellow-500/30 bg-yellow-500/10 p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="relative overflow-hidden rounded-3xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 via-slate-900 to-slate-950 p-5 shadow-2xl">
                    <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-yellow-500/10 blur-3xl"></div>
                    <div className="relative z-10">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="grid grid-cols-[auto_1fr] gap-4 items-start">
                          <div className="rounded-2xl bg-yellow-500/20 p-4 text-yellow-300 shadow-lg shadow-yellow-500/10">
                            <i className="fa-solid fa-star text-2xl"></i>
                          </div>
                          <div>
                            <h3 className="text-sm lg:text-lg font-black tracking-wide text-yellow-200">
                              Win Recognition & Performance Benefits
                            </h3>
                            <p className="mt-1 text-xs uppercase tracking-[3px] text-yellow-500">
                              SAKSHAM MSME Leadership Ecosystem
                            </p>
                          </div>
                        </div>
                        <div className="w-fit rounded-full border border-teal-500/30 bg-teal-500/10 px-4 py-2 text-xs font-bold text-teal-300">
                          AI Powered MSME Ranking
                        </div>
                      </div>
                     {/* CONTENT */}
                      <div className="mt-6 space-y-5">
                        <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-4">
                          <p className="text-sm leading-7 text-gray-200">
                            Active participation in the
                            <span className="mx-1 font-bold text-yellow-300">
                              Think5Whys SAKSHAM MSME Program
                            </span> through
                            <span className="mx-1 font-semibold text-teal-300">
                              AI-assisted task management
                            </span>,
                            <span className="mx-1 font-semibold text-teal-300">
                              RCA practices
                            </span>,
                            <span className="mx-1 font-semibold text-teal-300">
                              productivity improvements
                            </span>
                            and
                            <span className="mx-1 font-semibold text-teal-300">
                              Monthly AI Signals
                            </span>
                            may help MSMEs improve their
                            <span className="mx-1 font-bold text-yellow-300">
                              Think5Whys MSME Leadership Ranking Score
                            </span>.
                          </p>
                        </div>
                        <div className="grid gap-4 lg:grid-cols-2">
                          {/* LEFT CARD */}
                          <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
                            <div className="mb-4 flex items-center gap-3">
                              <div className="rounded-xl bg-yellow-500/10 p-3 text-yellow-300">
                                <i className="fa-solid fa-chart-line"></i>
                              </div>
                              <h4 className="text-xs lg:text-sm font-bold uppercase tracking-wide text-yellow-200">
                                Platform Recognition Benefits
                              </h4>
                            </div>
                            <ul className="space-y-3 text-sm text-gray-200 [&_li]:text-gray-100 ">
                              <li className="flex gap-3">
                                <i className="fa-solid fa-circle-check mt-1 text-teal-400"></i>
                                Ecosystem Visibility & MSME Leadership Recognition
                              </li>
                              <li className="flex gap-3">
                                <i className="fa-solid fa-circle-check mt-1 text-teal-400"></i>
                                Community Recognition Rewards & AI Excellence Badges
                              </li>
                              <li className="flex gap-3">
                                <i className="fa-solid fa-circle-check mt-1 text-teal-400"></i>
                                Learning Benefits & Growth Incentive Programs
                              </li>
                              <li className="flex gap-3">
                                <i className="fa-solid fa-circle-check mt-1 text-teal-400"></i>
                                Think5Whys-funded Promotional Support Initiatives
                              </li>
                            </ul>
                          </div>
                          {/* RIGHT CARD */}
                          <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-5">
                            <div className="mb-4 flex items-center gap-3">
                              <div className="rounded-xl bg-yellow-500/10 p-3 text-yellow-300">
                                <i className="fa-solid fa-indian-rupee-sign"></i>
                              </div>
                              <h4 className="text-sm font-bold uppercase tracking-wide text-yellow-200">
                                Special Promotional Support
                              </h4>
                            </div>
                            <ul className="space-y-3 text-sm text-gray-200 [&_li]:text-gray-100 ">
                              <li className="flex gap-3">
                                <i className="fa-solid fa-star mt-1 text-yellow-400"></i>
                                Partial MSME Loan Interest Support
                              </li>
                              <li className="flex gap-3">
                                <i className="fa-solid fa-star mt-1 text-yellow-400"></i>
                                GST Reimbursement Assistance
                              </li>
                              <li className="flex gap-3">
                                <i className="fa-solid fa-star mt-1 text-yellow-400"></i>
                                AI Adoption Incentives
                              </li>
                              <li className="flex gap-3">
                                <i className="fa-solid fa-star mt-1 text-yellow-400"></i>
                                Operational Excellence Rewards
                              </li>
                              <li className="flex gap-3">
                                <i className="fa-solid fa-star mt-1 text-yellow-400"></i>
                                Ecosystem Growth Benefits
                              </li>
                            </ul>
                          </div>
                        </div>
                        {/* FOOTER */}
                        <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <p className="text-sm leading-7 text-gray-300">
                              Leaderboard rankings are generated using
                              <span className="mx-1 font-bold text-yellow-300">
                                Think5Whys AI Evaluation Models
                              </span>
                              based on multiple operational, engagement, productivity and performance parameters tracked within the platform ecosystem.
                            </p>
                            <div className="w-fit rounded-full bg-teal-500/10 px-4 py-2 text-xs font-bold text-teal-300">
                              Monthly AI Signals Enabled
                            </div>
                          </div>
                          <p className="mt-4 text-xs leading-6 text-gray-500">
                            Rewards, reimbursement support models, recognition programs, scoring parameters and eligibility criteria may evolve periodically as part of ongoing platform innovation, marketing initiatives and MSME engagement programs.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* LEADERBOARD */}
              <div className="mt-4">
                <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div>
                    <h3 className="text-sm lg:text-lg font-bold text-yellow-200">
                      <i className="fa-solid fa-ranking-star"></i>
                      Top MSME Leaders of the Month 
                    </h3>
                    <p className="text-xs lg:text-sm text-gray-400">
                      Sample leaderboard for active MSME operational excellence users
                    </p>
                  </div>
                </div>
                {/* MOBILE CARDS */}
                <div className="space-y-4 lg:hidden">
                  {topMsmeLeaders.map((leader, index) => (
                    <div key={index} className="rounded-3xl border border-slate-800 bg-slate-950 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-gray-400">
                            Rank
                          </p>
                          <p className="text-sm lg:text-lg font-bold text-yellow-200">
                            #{index + 1}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-gray-400">
                            Points
                          </p>
                          <p className="text-sm lg:text-lg font-bold text-teal-300">
                            {leader.points.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-base font-bold text-white">
                          {leader.name}
                        </p>
                        <p className="mt-1 text-xs lg:text-sm text-gray-400">
                          {leader.place}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* DESKTOP TABLE */}
                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-3xl border border-slate-800">
                    <table className="min-w-full">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="px-5 py-4 text-left text-sm font-bold text-yellow-200">
                            Rank
                          </th>
                          <th className="px-5 py-4 text-left text-sm font-bold text-yellow-200">
                            MSME Leader
                          </th>
                          <th className="px-5 py-4 text-left text-sm font-bold text-yellow-200">
                            Location
                          </th>
                          <th className="px-5 py-4 text-left text-sm font-bold text-yellow-200">
                            Points Earned
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topMsmeLeaders.map((leader, index) => (
                          <tr
                            key={index}
                            className="border-t border-slate-800 bg-slate-950 hover:bg-slate-900"
                          >
                            <td className="px-5 py-4 text-sm font-semibold text-yellow-200">
                              #{index + 1}
                            </td>
                            <td className="px-5 py-4 text-sm text-white">
                              {leader.name}
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-300">
                              {leader.place}
                            </td>
                            <td className="px-5 py-4 text-sm font-bold text-teal-300">
                              {leader.points.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            <div ref={reportsSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-2 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-chart-column text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-sm lg:text-lg font-black text-yellow-200">{t('productivityTrend')}</h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">{t('weeklyAnalytics')}</p>
                </div>
              </div>
              <div className="h-72">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>

            <div ref={aiInsightsSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <MSMEOwnerAI selectedLanguage={selectedLanguage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}